import { useEffect, useRef, useState } from 'react';

/**
 * Inline display for (usually truncated) technical identifiers — MeLo, MaLo,
 * Zählernummern, SMGW-Serials. The shortened label stays as-is; the FULL value
 * is always one click away: click copies it to the clipboard (for lookups in
 * external systems), the tooltip shows it in full.
 *
 * Works without the async Clipboard API too (plain-HTTP deployments): falls
 * back to a hidden textarea + execCommand('copy').
 */
export function CopyableId({
  value,
  children,
  className,
  title,
}: {
  /** The full identifier that gets copied. */
  value: string;
  /** Shortened display; defaults to the full value. */
  children?: React.ReactNode;
  className?: string;
  /** Extra tooltip context, shown before the copy hint. */
  title?: string;
}) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
  }, []);

  const copy = async (e: React.MouseEvent) => {
    // Detail rows are often clickable themselves — copying must not select/navigate.
    e.stopPropagation();
    e.preventDefault();
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        ok = true;
      }
    } catch {
      // fall through to the legacy path
    }
    if (!ok) {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand('copy'); } catch { ok = false; }
      document.body.removeChild(ta);
    }
    if (ok) {
      setCopied(true);
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
      resetTimer.current = window.setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Kopiert!' : `${title ? `${title}\n` : ''}${value}\nKlicken zum Kopieren`}
      className={`inline-flex items-center gap-1 min-w-0 cursor-copy hover:text-accent-primary dark:hover:text-accent-primary transition-colors text-left ${className ?? ''}`}
    >
      <span className="truncate">{children ?? value}</span>
      {copied ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-3 h-3 shrink-0 text-success-600 dark:text-success-400" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-3 h-3 shrink-0 opacity-40" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 8V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2M6 8h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z" />
        </svg>
      )}
    </button>
  );
}
